import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PricingCard } from './PricingCard';
import { FAQ } from '../landing';
import { subscriptionService } from '../../api/services';
import { useAuth } from '../../contexts/AuthContext';
import type { SubscriptionPlan, BillingPeriod } from '../../types';
import { Loader2 } from 'lucide-react';

const faqs = [
  {
    question: 'Can I change plans later?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.'
  },
  {
    question: 'What happens when I exceed my scan limit?',
    answer: 'On the Free plan, scans will be paused until the next billing cycle. Paid plans have unlimited scans.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'Yes, we offer a 30-day money-back guarantee for all paid plans, no questions asked.'
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Absolutely. You can cancel your subscription at any time with no cancellation fees.'
  },
  {
    question: 'Is there a setup fee?',
    answer: 'No setup fees ever. You only pay the monthly or annual subscription price.'
  },
  {
    question: 'Do you offer volume discounts?',
    answer: 'Yes, we offer custom pricing for enterprise needs. Contact our sales team for details.'
  }
];

export function PricingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isAnnual, setIsAnnual] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const plansData = await subscriptionService.getPlans();
      setPlans(plansData.sort((a, b) => a.sort_order - b.sort_order));
    } catch (error) {
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (plan: SubscriptionPlan) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (plan.name === 'free') {
      return;
    }

    setCheckoutLoading(plan.name);
    try {
      const response = await subscriptionService.createCheckoutSession({
        plan_name: plan.name as 'pro' | 'business',
        billing_period: isAnnual ? 'annual' : 'monthly',
      });
      window.location.href = response.checkout_url;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const currentPlanName = user?.subscription?.plan?.name || (isAuthenticated ? 'free' : undefined);
  const billingPeriod: BillingPeriod = isAnnual ? 'annual' : 'monthly';

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[--clinqr-primary]" />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen section-padding">
      <div className="container-page">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="heading-display text-[--clinqr-navy] mb-4">
            Simple, transparent pricing
          </h1>
          <p className="body-lg mb-8">
            Start free, upgrade as you grow
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`${!isAnnual ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-secondary]'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className="relative w-14 h-7 rounded-full transition-colors duration-200"
              style={{
                backgroundColor: isAnnual ? 'var(--clinqr-primary)' : 'var(--clinqr-border)'
              }}
            >
              <div
                className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-200"
                style={{
                  transform: isAnnual ? 'translateX(28px)' : 'translateX(0)'
                }}
              />
            </button>
            <span className={`${isAnnual ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-secondary]'} flex items-center gap-2`}>
              Annual
              <span className="px-3 py-1 bg-[--clinqr-navy] text-[--clinqr-lime] rounded-full text-xs font-bold shadow-sm">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              billingPeriod={billingPeriod}
              isCurrentPlan={plan.name === currentPlanName}
              onAction={handleAction}
              loading={checkoutLoading === plan.name}
              currentPlanName={currentPlanName}
              plans={plans}
            />
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <FAQ faqs={faqs} />
    </div>
  );
}
